<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import SingleProductRatingForm from "@apps/reviews/components/ReviewForm/SingleProductRatingForm.svelte";
    import { REVIEW_TYPE } from "@apps/reviews/constants";
    import CanLeaveReviewQuery from "@apps/reviews/widgets/review_form_modal_widget/queries/CanLeaveReviewQuery";
    import FormManager from "@lib/FormManager/FormManager";
    import { ValidationStrategy } from "@lib/FormManager/types";
    import yupResolver from "@lib/FormManager/yupResolver";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import { createEventDispatcher, onMount } from "svelte";
    import * as Yup from "yup";

    import Spinner from "@components/Spinner/Spinner.svelte";
    import { stepperControllerContextToken } from "@components/Stepper/context_tokens";
    import T from "@components/T.svelte";

    import {
        CAN_LEAVE_REVIEW_MESSAGE_KEY,
        LEAVE_REVIEW_SOURCE,
        REVIEWS_FORM_STEPS_NAMES,
    } from "../../../constants";
    import ReviewsWizardManager from "../../../managers/ReviewsWizardManager";
    import PurchasedProductsQuery from "../../../queries/PurchasedProductsQuery";
    import PurchasedProductsForm from "./PurchasedProductsForm.svelte";
    import ReviewsFormLoginButton from "./ReviewsFormLoginButton.svelte";

    export let productId: number | undefined;
    export let canLeaveReviewQuery: CanLeaveReviewQuery;
    export let reviewsWizardManager: ReviewsWizardManager;
    export let showPurchasedProducts: boolean;
    export let isAuthenticated: boolean;
    export let showPurchasedProductsInitial: boolean;
    export let purchasedProductsQuery: PurchasedProductsQuery;
    export let reviewType: REVIEW_TYPE | undefined;
    export let source: LEAVE_REVIEW_SOURCE;

    const stepperController = getTypedContext(stepperControllerContextToken);
    const canLeaveReview$ = canLeaveReviewQuery.canLeaveReview$;
    const canLeaveReviewStatus$ = canLeaveReviewQuery.canLeaveReviewStatus$;
    const canLeaveReviewMessageKey$ =
        canLeaveReviewQuery.canLeaveReviewMessageKey$;
    const purchasedProducts$ = purchasedProductsQuery.all$;
    const purchasedProductsStatus$ = purchasedProductsQuery.status$;

    const dispatch = createEventDispatcher();

    const formManager = reviewsWizardManager.cacheManager.get(
        REVIEWS_FORM_STEPS_NAMES.RATING,
        [],
        () => {
            return new FormManager({
                initialValues: { rating: "" },
                resolver: yupResolver(
                    Yup.object({
                        rating: Yup.number()
                            .min(1, "validation_rating")
                            .required("validation_field_required"),
                    })
                ),
                unregisterOnUnmount: false,
                validationStrategy: ValidationStrategy.OnSubmit,
                onSubmit: ({ values, errors }) => {
                    if (!values || (errors && Object.values(errors).length > 0))
                        return;

                    reviewsWizardManager.collectFormData(values);
                    stepperController.nextStep();
                },
            });
        }
    );

    onMount(() => {
        reviewsWizardManager.setPrevButton(undefined);

        // В норме unregister("rating") должен вызываться в onDestroy дочернего компонента FormManagerProvider
        // который находится внутри SingleRatingForm.
        // Но из-за бага Svelte onDestroy (в данном случае — функция, возвращаемая из onMount) может не срабатывать,
        // если компонент находится внутри блока {#if}.
        // Поэтому выполняем ручную очистку здесь, в родителе, как fallback.
        return () => {
            if (
                formManager.fields$
                    .getValue()
                    .some((field) => field.name === "rating")
            ) {
                formManager.unregister("rating");
            }
        };
    });

    $: {
        if (
            showPurchasedProducts &&
            $purchasedProductsStatus$ === STATUS.READY &&
            $purchasedProducts$.length === 0
        ) {
            dispatch("updateShowPurchasedProducts", { value: false });
        }
    }

    $: {
        if (
            isAuthenticated &&
            !showPurchasedProductsInitial &&
            $canLeaveReviewStatus$ === STATUS.READY &&
            !$canLeaveReview$ &&
            reviewType === REVIEW_TYPE.SITE_REVIEW
        ) {
            dispatch("updateShowPurchasedProducts", { value: true });
        }
    }

    const checkShouldShowSpinner = (
        showPurchasedProducts: boolean,
        canLeaveReviewStatus: STATUS,
        purchasedProductsStatus: STATUS
    ) => {
        if (!showPurchasedProducts && canLeaveReviewStatus === STATUS.LOADING) {
            return true;
        }

        if (
            showPurchasedProducts &&
            purchasedProductsStatus === STATUS.LOADING
        ) {
            return true;
        }

        return false;
    };

    $: shouldShowSpinner = checkShouldShowSpinner(
        showPurchasedProducts,
        $canLeaveReviewStatus$,
        $purchasedProductsStatus$
    );
    $: shouldShowPurchasedProductsForm = showPurchasedProducts;
    $: shouldShowSingleProductRatingForm = $canLeaveReview$;
    $: shouldShowCanLeaveReviewMessage =
        $canLeaveReviewStatus$ === STATUS.READY;
</script>

{#if shouldShowSpinner}
    <div class="gw-rv-rating-step__loading">
        <Spinner />
    </div>
{:else if shouldShowPurchasedProductsForm}
    <PurchasedProductsForm
        {formManager}
        purchasedProducts={$purchasedProducts$}
        {source}
        on:updateProductId
    />
{:else if shouldShowSingleProductRatingForm}
    <SingleProductRatingForm {formManager} {productId} {reviewType} {source} />
{:else if shouldShowCanLeaveReviewMessage}
    <div class="gw-rv-rating-step__error">
        <span class="gw-rv-rating-step__error-message">
            {#if $canLeaveReviewMessageKey$}
                <T
                    namespace="reviews.ReviewsFormModalWidget"
                    translationKey={$canLeaveReviewMessageKey$}
                />
            {:else}
                Something went wrong!
            {/if}
        </span>
        {#if $canLeaveReviewMessageKey$ === CAN_LEAVE_REVIEW_MESSAGE_KEY.ONLY_LOGGED_IN_CUSTOMER_CAN_LEAVE_REVIEW}
            <ReviewsFormLoginButton />
        {/if}
    </div>
{/if}

<style lang="postcss">
    .gw-rv-rating-step {
        &__loading {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        &__error {
            display: flex;
            height: 100%;
            justify-content: center;
            align-items: center;
            font-size: 1.25em;
            padding: 2rem;
            flex-direction: column;
            row-gap: 1em;
        }

        &__error-message {
            display: inline-block;
            text-align: center;

            @media screen and (max-width: 600px) {
                width: 100%;
            }
        }
    }
</style>
